/**
 * Validation utility functions for React Hook Form
 * Provides reusable validation rules with i18n support
 */

/**
 * Email validation rule
 * @param {Function} t - Translation function from i18n
 * @returns {Object} Validation rule object for react-hook-form
 */
export const emailValidation = (t) => ({
  required: t('validation.emailRequired'),
  validate: {
    hasAtSign: (value) => {
      if (!value.includes('@')) {
        return t('validation.emailMissingAt');
      }
      return true;
    },
    validFormat: (value) => {
      const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailPattern.test(value)) {
        return t('validation.emailInvalid');
      }
      return true;
    }
  }
});

/**
 * Password validation rule
 * @param {Function} t - Translation function from i18n
 * @param {number} minLength - Minimum password length (default: 6)
 * @returns {Object} Validation rule object for react-hook-form
 */
export const passwordValidation = (t, minLength = 6) => ({
  required: t('validation.passwordRequired'),
  minLength: {
    value: minLength,
    message: t('validation.passwordMinLength', { count: minLength }),
  },
});

/**
 * Password confirmation validation rule
 * @param {Function} t - Translation function from i18n
 * @param {string} passwordValue - Value of the password field to compare with
 * @returns {Object} Validation rule object for react-hook-form
 */
export const passwordConfirmValidation = (t, passwordValue) => ({
  required: t('validation.passwordConfirmRequired'),
  validate: (value) =>
    value === passwordValue || t('validation.passwordsMismatch'),
});

/**
 * Required field validation rule
 * @param {Function} t - Translation function from i18n
 * @param {string} fieldName - Name of the field for error message
 * @returns {Object} Validation rule object for react-hook-form
 */
export const requiredValidation = (t, fieldName) => ({
  required: {
    value: true,
    message: t('validation.fieldRequired', { field: fieldName }),
  },
});

/**
 * URL validation rule
 * @param {Function} t - Translation function from i18n
 * @param {boolean} required - Whether the field is required (default: false)
 * @returns {Object} Validation rule object for react-hook-form
 */
export const urlValidation = (t, required = false) => {
  const rules = {
    pattern: {
      value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i,
      message: t('validation.urlInvalid'),
    },
  };

  if (required) {
    rules.required = t('validation.urlRequired');
  }

  return rules;
};

/**
 * Phone validation rule (basic international format)
 * @param {Function} t - Translation function from i18n
 * @param {boolean} required - Whether the field is required (default: false)
 * @returns {Object} Validation rule object for react-hook-form
 */
export const phoneValidation = (t, required = false) => {
  const rules = {
    pattern: {
      value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
      message: t('validation.phoneInvalid'),
    },
  };

  if (required) {
    rules.required = t('validation.phoneRequired');
  }

  return rules;
};

/**
 * Minimum length validation rule
 * @param {Function} t - Translation function from i18n
 * @param {number} minLength - Minimum length
 * @param {string} fieldName - Name of the field for error message
 * @returns {Object} Validation rule object for react-hook-form
 */
export const minLengthValidation = (t, minLength, fieldName) => ({
  minLength: {
    value: minLength,
    message: t('validation.minLength', { field: fieldName, count: minLength }),
  },
});

/**
 * Maximum length validation rule
 * @param {Function} t - Translation function from i18n
 * @param {number} maxLength - Maximum length
 * @param {string} fieldName - Name of the field for error message
 * @returns {Object} Validation rule object for react-hook-form
 */
export const maxLengthValidation = (t, maxLength, fieldName) => ({
  maxLength: {
    value: maxLength,
    message: t('validation.maxLength', { field: fieldName, count: maxLength }),
  },
});

/**
 * Date validation rule (checks if date is not in the future)
 * @param {Function} t - Translation function from i18n
 * @returns {Object} Validation rule object for react-hook-form
 */
export const pastDateValidation = (t) => ({
  validate: (value) => {
    if (!value) return true;
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate <= today || t('validation.dateFuture');
  },
});
